import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';

const INVALID_SHEET_CHARS = /[\\/?*\[\]:]/g;
const MAX_SHEET_NAME_LENGTH = 31;
const METADATA_TAB_COLOR = 'A6A6A6';

const COLORS = {
    titleBg: '1F4E78',
    titleFont: 'FFFFFF',
    headerBg: 'DCE6F1',
    headerFont: '1F4E78',
    keyBg: 'EEF3FA',
    keyFont: '203864',
    tooltipKeyFont: '2F5597',
    border: 'D9D9D9'
};

const STYLES = {
    title: {
        font: { bold: true, sz: 14, color: { rgb: COLORS.titleFont } },
        fill: { fgColor: { rgb: COLORS.titleBg } },
        alignment: { horizontal: 'left', vertical: 'center' }
    },
    header: {
        font: { bold: true, sz: 11, color: { rgb: COLORS.headerFont } },
        fill: { fgColor: { rgb: COLORS.headerBg } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
    },
    keyCell: {
        font: { bold: true, sz: 10, color: { rgb: COLORS.keyFont } },
        fill: { fgColor: { rgb: COLORS.keyBg } },
        alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
    },
    tooltipKeyCell: {
        font: { bold: true, sz: 10, color: { rgb: COLORS.tooltipKeyFont } },
        fill: { fgColor: { rgb: COLORS.keyBg } },
        alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
    },
    bodyCell: {
        font: { sz: 10, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center', wrapText: false }
    },
    bodyCellAlt: {
        font: { sz: 10, color: { rgb: '1F1F1F' } },
        fill: { fgColor: { rgb: 'F9FBFD' } },
        alignment: { horizontal: 'left', vertical: 'center', wrapText: false }
    },
    numericCell: {
        font: { sz: 10, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'right', vertical: 'center' }
    },
    numericCellAlt: {
        font: { sz: 10, color: { rgb: '1F1F1F' } },
        fill: { fgColor: { rgb: 'F9FBFD' } },
        alignment: { horizontal: 'right', vertical: 'center' }
    }
};

const THIN_BORDER = {
    top: { style: 'thin', color: { rgb: COLORS.border } },
    right: { style: 'thin', color: { rgb: COLORS.border } },
    bottom: { style: 'thin', color: { rgb: COLORS.border } },
    left: { style: 'thin', color: { rgb: COLORS.border } }
};

function sanitizeSheetName(sheetName, fallbackName) {
    const normalized = (sheetName || fallbackName || 'Sheet')
        .replace(INVALID_SHEET_CHARS, ' ')
        .trim();

    return (normalized || 'Sheet').slice(0, MAX_SHEET_NAME_LENGTH);
}

function normalizeCellValue(value) {
    if (value === null || value === undefined) {
        return '';
    }

    if (Array.isArray(value)) {
        return value.join(', ');
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return value;
}

function inferColumnFormat(column) {
    if (column?.format) {
        return column.format;
    }

    const key = String(column?.key || '').toLowerCase();
    const label = String(column?.label || '').toLowerCase();
    const looksPercent = key.includes('vs') || label.includes('%');
    const looksCurrency = (key.includes('sales') || key.includes('target') || key === 'ads') && !looksPercent;

    if (looksPercent) {
        return { type: 'percent', decimals: 2 };
    }

    if (looksCurrency) {
        return { type: 'currency', currencySymbol: '₱', decimals: 2 };
    }

    return null;
}

function getNumberFormat(format) {
    const decimals = Number.isInteger(format?.decimals) ? format.decimals : 2;

    if (format?.type === 'currency') {
        return `"${format?.currencySymbol || '₱'}"#,##0.${'0'.repeat(decimals)}`;
    }

    if (format?.type === 'percent') {
        return `#,##0.${'0'.repeat(decimals)}"%"`;
    }

    if (format?.type === 'number') {
        return `#,##0.${'0'.repeat(decimals)}`;
    }

    return null;
}

function isNumberLike(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return true;
    }

    if (typeof value !== 'string') {
        return false;
    }

    const cleaned = value.replace(/,/g, '').replace(/%/g, '').trim();
    if (cleaned === '') {
        return false;
    }

    return Number.isFinite(Number(cleaned));
}

function coerceNumericValue(value) {
    if (typeof value === 'number') {
        return value;
    }

    if (typeof value !== 'string') {
        return value;
    }

    const cleaned = value.replace(/,/g, '').replace(/%/g, '').trim();
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : value;
}

function applyColumnWidths(worksheet, rows, options = {}) {
    const { headerRowIndex = -1, dataStartRowIndex = -1, dataEndRowIndex = -1 } = options;
    const maxCols = Math.max(...rows.map((row) => row.length), 1);
    worksheet['!cols'] = Array.from({ length: maxCols }, (_, colIdx) => {
        const headerLen = headerRowIndex >= 0
            ? String(normalizeCellValue(rows?.[headerRowIndex]?.[colIdx] ?? '')).length
            : 0;

        const hasDataRows = dataStartRowIndex >= 0 && dataEndRowIndex >= dataStartRowIndex;
        const contentLen = hasDataRows
            ? rows.slice(dataStartRowIndex, dataEndRowIndex + 1).reduce((acc, row) => {
                const value = row?.[colIdx];
                const text = normalizeCellValue(value);
                return Math.max(acc, String(text).length);
            }, 0)
            : rows.reduce((acc, row) => {
                const value = row?.[colIdx];
                const text = normalizeCellValue(value);
                return Math.max(acc, String(text).length);
            }, 0);

        // Prioritize actual table content over long header labels.
        const maxLen = hasDataRows
            ? Math.max(contentLen, Math.min(headerLen, 18))
            : Math.max(contentLen, headerLen);

        return { wch: Math.min(Math.max(maxLen + 2, 8), 56) };
    });
}

function buildSheetStructure(section) {
    const rows = [];
    const columns = section.columns || [];
    const infoValueMergeRowIndexes = [];
    let descriptionRowIndex = -1;
    let tooltipsHeaderRowIndex = -1;

    rows.push([section.title || section.sheetName || 'Section']);

    if (section.description) {
        rows.push(['Description', section.description]);
        descriptionRowIndex = rows.length - 1;
        infoValueMergeRowIndexes.push(descriptionRowIndex);
    }

    if (section.tooltips?.length) {
        rows.push(['Tooltips']);
        tooltipsHeaderRowIndex = rows.length - 1;
        section.tooltips.forEach((tooltip) => {
            rows.push([tooltip.label || 'Info', tooltip.text || '']);
            infoValueMergeRowIndexes.push(rows.length - 1);
        });
    }

    rows.push([]);
    const headerRowIndex = rows.length;
    rows.push(columns.map((column) => column.label || column.key));

    const dataStartRowIndex = rows.length;
    const tooltipLabelRowIndexes = [];
    if (section.tooltips?.length) {
        const firstTooltipRow = section.description ? 3 : 2;
        for (let idx = 0; idx < section.tooltips.length; idx += 1) {
            tooltipLabelRowIndexes.push(firstTooltipRow + idx);
        }
    }

    (section.rows || []).forEach((row) => {
        rows.push(columns.map((column) => {
            const format = inferColumnFormat(column);
            const rawValue = normalizeCellValue(row?.[column.key]);

            if (format && isNumberLike(rawValue)) {
                return coerceNumericValue(rawValue);
            }

            return rawValue;
        }));
    });
    const dataEndRowIndex = rows.length - 1;

    return {
        rows,
        columns,
        headerRowIndex,
        dataStartRowIndex,
        dataEndRowIndex,
        tooltipLabelRowIndexes,
        descriptionRowIndex,
        tooltipsHeaderRowIndex,
        infoValueMergeRowIndexes
    };
}

function normalizeTabColor(tabColor) {
    if (!tabColor) {
        return null;
    }

    const cleaned = String(tabColor).replace('#', '').trim().toUpperCase();
    if (!/^[0-9A-F]{6}([0-9A-F]{2})?$/.test(cleaned)) {
        return null;
    }

    return cleaned.length === 6 ? `FF${cleaned}` : cleaned;
}

function buildSheetTabColorMap(workbook, requestedTabColors = {}) {
    const map = {};
    Object.entries(requestedTabColors).forEach(([sheetName, rawColor]) => {
        const normalizedColor = normalizeTabColor(rawColor);
        if (!normalizedColor) {
            return;
        }

        const sheetIndex = (workbook.SheetNames || []).indexOf(sheetName);
        if (sheetIndex >= 0) {
            map[sheetIndex + 1] = normalizedColor;
        }
    });

    return map;
}

function injectTabColorIntoWorksheetXml(worksheetXml, argbColor) {
    if (!worksheetXml || !argbColor) {
        return worksheetXml;
    }

    const tabColorNode = `<tabColor rgb="${argbColor}"/>`;
    if (/<sheetPr\b[\s\S]*?<\/sheetPr>/.test(worksheetXml)) {
        if (/<tabColor\b[^>]*\/>/.test(worksheetXml)) {
            return worksheetXml.replace(/<tabColor\b[^>]*\/>/, tabColorNode);
        }

        return worksheetXml.replace(/<sheetPr\b([^>]*)>/, `<sheetPr$1>${tabColorNode}`);
    }

    return worksheetXml.replace(/<worksheet\b([^>]*)>/, `<worksheet$1><sheetPr>${tabColorNode}</sheetPr>`);
}

async function writeWorkbookWithTabColors({ workbook, fileName, requestedTabColors }) {
    const sheetTabColorMap = buildSheetTabColorMap(workbook, requestedTabColors);
    const hasTabColors = Object.keys(sheetTabColorMap).length > 0;

    if (!hasTabColors || typeof window === 'undefined' || typeof document === 'undefined') {
        XLSX.writeFile(workbook, `${fileName || 'dashboard-export'}.xlsx`);
        return;
    }

    const workbookArray = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    const zip = await JSZip.loadAsync(workbookArray);

    const patchPromises = Object.entries(sheetTabColorMap).map(async ([sheetNumber, argbColor]) => {
        const worksheetPath = `xl/worksheets/sheet${sheetNumber}.xml`;
        const worksheetFile = zip.file(worksheetPath);
        if (!worksheetFile) {
            return;
        }

        const worksheetXml = await worksheetFile.async('string');
        const patchedXml = injectTabColorIntoWorksheetXml(worksheetXml, argbColor);
        zip.file(worksheetPath, patchedXml);
    });

    await Promise.all(patchPromises);

    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `${fileName || 'dashboard-export'}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function collectSheetTabColor(requestedTabColors, sheetName, tabColor) {
    if (!tabColor || !sheetName) {
        return;
    }

    requestedTabColors[sheetName] = tabColor;
}

function applyStyle(worksheet, rowIndex, colIndex, style, withBorder = false) {
    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
    const cell = worksheet[cellAddress];

    if (!cell) {
        return;
    }

    const nextStyle = { ...style };
    if (withBorder) {
        nextStyle.border = THIN_BORDER;
    }

    cell.s = nextStyle;
}

function applyWorksheetFormatting(worksheet, structure) {
    const {
        columns,
        headerRowIndex,
        dataStartRowIndex,
        dataEndRowIndex,
        tooltipLabelRowIndexes,
        descriptionRowIndex,
        tooltipsHeaderRowIndex,
        infoValueMergeRowIndexes
    } = structure;

    const merges = [];

    if (columns.length > 1) {
        merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: columns.length - 1 } });
    }

    if (columns.length > 2) {
        infoValueMergeRowIndexes.forEach((rowIndex) => {
            merges.push({ s: { r: rowIndex, c: 1 }, e: { r: rowIndex, c: columns.length - 1 } });
        });
    }

    if (merges.length > 0) {
        worksheet['!merges'] = merges;
    }

    if (columns.length > 0) {
        const startRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: 0 });
        const endRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: columns.length - 1 });
        worksheet['!autofilter'] = { ref: `${startRef}:${endRef}` };
    }

    // Title row
    applyStyle(worksheet, 0, 0, STYLES.title, true);

    // Description / tooltips key rows on first column
    if (descriptionRowIndex >= 0) {
        applyStyle(worksheet, descriptionRowIndex, 0, STYLES.keyCell, true);
    }
    if (tooltipsHeaderRowIndex >= 0) {
        applyStyle(worksheet, tooltipsHeaderRowIndex, 0, STYLES.keyCell, true);
    }
    tooltipLabelRowIndexes.forEach((rowIndex) => {
        applyStyle(worksheet, rowIndex, 0, STYLES.tooltipKeyCell, true);
    });

    infoValueMergeRowIndexes.forEach((rowIndex) => {
        applyStyle(worksheet, rowIndex, 1, STYLES.bodyCell, true);
    });

    // Header row
    for (let colIdx = 0; colIdx < columns.length; colIdx += 1) {
        applyStyle(worksheet, headerRowIndex, colIdx, STYLES.header, true);
    }

    columns.forEach((column, colIdx) => {
        const columnFormat = inferColumnFormat(column);
        const numberFormat = getNumberFormat(columnFormat);

        if (!numberFormat || dataStartRowIndex > dataEndRowIndex) {
            return;
        }

        for (let rowIdx = dataStartRowIndex; rowIdx <= dataEndRowIndex; rowIdx += 1) {
            const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
            const cell = worksheet[cellAddress];

            if (!cell) {
                continue;
            }

            const isAltRow = (rowIdx - dataStartRowIndex) % 2 === 1;
            const baseStyle = typeof cell.v === 'number'
                ? (isAltRow ? STYLES.numericCellAlt : STYLES.numericCell)
                : (isAltRow ? STYLES.bodyCellAlt : STYLES.bodyCell);
            cell.s = { ...baseStyle, border: THIN_BORDER };

            if (typeof cell.v !== 'number') {
                continue;
            }

            cell.z = numberFormat;
        }
    });

    // Apply body style to unformatted columns too
    for (let rowIdx = dataStartRowIndex; rowIdx <= dataEndRowIndex; rowIdx += 1) {
        const isAltRow = (rowIdx - dataStartRowIndex) % 2 === 1;
        for (let colIdx = 0; colIdx < columns.length; colIdx += 1) {
            const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
            const cell = worksheet[cellAddress];
            if (!cell || cell?.s) {
                continue;
            }

            const bodyStyle = typeof cell.v === 'number'
                ? (isAltRow ? STYLES.numericCellAlt : STYLES.numericCell)
                : (isAltRow ? STYLES.bodyCellAlt : STYLES.bodyCell);
            cell.s = { ...bodyStyle, border: THIN_BORDER };
        }
    }

    worksheet['!rows'] = [
        { hpt: 24 },
        { hpt: 18 },
        { hpt: 18 }
    ];
}

function appendMetadataSheet(workbook, metadata) {
    const metadataRows = [['Metadata']];

    Object.entries(metadata || {}).forEach(([key, value]) => {
        metadataRows.push([key, normalizeCellValue(value)]);
    });

    const metadataSheet = XLSX.utils.aoa_to_sheet(metadataRows);
    applyColumnWidths(metadataSheet, metadataRows);

    applyStyle(metadataSheet, 0, 0, STYLES.title, true);
    for (let rowIdx = 1; rowIdx < metadataRows.length; rowIdx += 1) {
        applyStyle(metadataSheet, rowIdx, 0, STYLES.keyCell, true);
        applyStyle(metadataSheet, rowIdx, 1, STYLES.bodyCell, true);
    }

    const metadataSheetName = sanitizeSheetName('Metadata', 'Metadata');
    XLSX.utils.book_append_sheet(workbook, metadataSheet, metadataSheetName);
    return metadataSheetName;
}

export async function exportDashboardWorkbook({ fileName, sections, metadata }) {
    const workbook = XLSX.utils.book_new();
    const requestedTabColors = {};

    if (metadata && Object.keys(metadata).length > 0) {
        const metadataSheetName = appendMetadataSheet(workbook, metadata);
        collectSheetTabColor(requestedTabColors, metadataSheetName, METADATA_TAB_COLOR);
    }

    (sections || []).forEach((section, index) => {
        const structure = buildSheetStructure(section);
        const worksheet = XLSX.utils.aoa_to_sheet(structure.rows);
        applyWorksheetFormatting(worksheet, structure);
        applyColumnWidths(worksheet, structure.rows, {
            headerRowIndex: structure.headerRowIndex,
            dataStartRowIndex: structure.dataStartRowIndex,
            dataEndRowIndex: structure.dataEndRowIndex
        });
        const safeSheetName = sanitizeSheetName(section.sheetName, `Section ${index + 1}`);

        XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);
        collectSheetTabColor(requestedTabColors, safeSheetName, section.tabColor);
    });

    await writeWorkbookWithTabColors({ workbook, fileName, requestedTabColors });
}
